/**
 * Cosméticos: escudo, uniforme, tema de estádio e título de treinador.
 *
 * Um cosmético aqui é **um punhado de parâmetros**, nunca um arquivo de
 * imagem. O escudo já nasceu assim no cliente (`src/ui/atoms.tsx` desenha a
 * partir de duas cores) e a frente de social escreveu o próprio codificador de
 * PNG em vez de subir binário. Seguir o mesmo caminho mantém o repositório sem
 * `.png`, o aplicativo leve e um cosmético novo a uma linha de distância.
 *
 * Por isso o catálogo mora neste pacote e não no banco: a semeadura cria os
 * produtos a partir daqui, a API resolve `sku` para parâmetros antes de
 * responder, e o cliente só desenha o que recebeu. Uma cor nova muda um lugar
 * só.
 *
 * O que **não** está aqui, de propósito: nada que altere força, moral, dinheiro
 * ou qualquer número que o motor leia. Cosmético que mexe em campo deixa de ser
 * cosmético e vira o que `docs/PLANO.md` 6 proíbe vender.
 */
import { z } from "zod";
import { IdSchema } from "./common.js";
// ---------------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------------
/** Os quatro tipos de cosmético, que são quatro lugares diferentes da tela. */
export const CosmeticKindSchema = z.enum(["crest", "kit", "stadium", "title"]);
/**
 * Cor em `#rrggbb`.
 *
 * Forma fechada, e não texto livre, porque estes valores entram direto em SVG e
 * em `style`. Aceitar `url(...)` ou `expression(...)` aqui seria abrir a tela
 * do jogo para o que um catálogo mal semeado escrevesse.
 */
export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Cor fora do formato #rrggbb.");
/** Contorno do escudo. */
export const CrestShapeSchema = z.enum(["shield", "circle", "diamond", "pennant"]);
/** Desenho de dentro do escudo. */
export const CrestPatternSchema = z.enum(["solid", "sash", "stripes", "halves", "star"]);
/** Desenho da camisa. */
export const KitPatternSchema = z.enum(["stripes", "sash", "hoops", "halves", "checks"]);
// ---------------------------------------------------------------------------
// Parâmetros de cada tipo
// ---------------------------------------------------------------------------
export const CrestParamsSchema = z
    .object({
    shape: CrestShapeSchema,
    pattern: CrestPatternSchema,
    primary: HexColorSchema,
    secondary: HexColorSchema,
    /** Contorno e detalhe fino, o que dá o acabamento em tamanho pequeno. */
    detail: HexColorSchema,
})
    .strict();
export const KitParamsSchema = z
    .object({
    pattern: KitPatternSchema,
    primary: HexColorSchema,
    secondary: HexColorSchema,
    /** Gola e punho. */
    detail: HexColorSchema,
})
    .strict();
/**
 * Tema do estádio.
 *
 * São as quatro cores do cabeçalho do clube: o gramado, a arquibancada, o céu
 * ao fundo e a luz dos refletores. Nenhuma delas chega perto de capacidade,
 * público ou renda, que continuam vindo do motor.
 */
export const StadiumParamsSchema = z
    .object({
    grass: HexColorSchema,
    stands: HexColorSchema,
    sky: HexColorSchema,
    lights: HexColorSchema,
})
    .strict();
/** Título que acompanha o nome do treinador. O texto é conteúdo do catálogo. */
export const TitleParamsSchema = z
    .object({
    label: z.string().min(1).max(24),
    color: HexColorSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------
const BaseDoCosmetico = z.object({
    /** Chave estável do item, e também o `sku` do produto que o vende. */
    sku: z.string().min(1).max(64),
    /** Nome visível, em português do Brasil. */
    name: z.string().min(1),
    description: z.string().min(1),
    /** Preço em centavos por moeda, igual a `products.prices`. */
    prices: z.record(z.number().int().nonnegative()),
});
/**
 * Um item do catálogo, com o tipo mandando na forma dos parâmetros.
 *
 * União discriminada e não um campo solto: assim um escudo com parâmetros de
 * uniforme não compila, em vez de virar uma camisa sem desenho na tela.
 */
export const CosmeticSchema = z.discriminatedUnion("kind", [
    BaseDoCosmetico.extend({ kind: z.literal("crest"), params: CrestParamsSchema }),
    BaseDoCosmetico.extend({ kind: z.literal("kit"), params: KitParamsSchema }),
    BaseDoCosmetico.extend({ kind: z.literal("stadium"), params: StadiumParamsSchema }),
    BaseDoCosmetico.extend({ kind: z.literal("title"), params: TitleParamsSchema }),
]);
/** Prefixo do `sku` de todo cosmético, para separá-lo do resto do catálogo. */
export const COSMETIC_SKU_PREFIX = "cosmetico-";
/**
 * O catálogo semeado.
 *
 * Modesto de propósito: quatro escudos, quatro uniformes, três estádios e
 * quatro títulos. Preço em real, em centavos, na mesma faixa dos produtos que
 * já existem, e em dólar para as lojas que cobram fora.
 *
 * A faixa é por tipo e não por item. Um escudo custar mais que outro porque é
 * "melhor" começaria a insinuar vantagem, e não existe vantagem nenhuma aqui.
 */
export const COSMETICS = [
    // Escudos ----------------------------------------------------------------
    {
        sku: "cosmetico-escudo-aurora",
        kind: "crest",
        name: "Escudo Aurora",
        description: "Faixa diagonal em azul e verde, com contorno claro.",
        prices: { BRL: 790, USD: 199 },
        params: {
            shape: "shield",
            pattern: "sash",
            primary: "#123f6d",
            secondary: "#2fbf9b",
            detail: "#e8f4ff",
        },
    },
    {
        sku: "cosmetico-escudo-tradicao",
        kind: "crest",
        name: "Escudo Tradição",
        description: "Redondo, partido ao meio, em vinho e creme.",
        prices: { BRL: 790, USD: 199 },
        params: {
            shape: "circle",
            pattern: "halves",
            primary: "#6d1226",
            secondary: "#efe0c4",
            detail: "#c9a227",
        },
    },
    {
        sku: "cosmetico-escudo-vanguarda",
        kind: "crest",
        name: "Escudo Vanguarda",
        description: "Losango listrado em grafite e laranja.",
        prices: { BRL: 790, USD: 199 },
        params: {
            shape: "diamond",
            pattern: "stripes",
            primary: "#23262e",
            secondary: "#ff7a29",
            detail: "#f2f4f8",
        },
    },
    {
        sku: "cosmetico-escudo-flamula",
        kind: "crest",
        name: "Escudo Flâmula",
        description: "Flâmula roxa com estrela dourada.",
        prices: { BRL: 790, USD: 199 },
        params: {
            shape: "pennant",
            pattern: "star",
            primary: "#3c1d63",
            secondary: "#7b4bb8",
            detail: "#ffd166",
        },
    },
    // Uniformes --------------------------------------------------------------
    {
        sku: "cosmetico-uniforme-listrado",
        kind: "kit",
        name: "Uniforme Listrado",
        description: "Listras verticais em preto e branco, gola vermelha.",
        prices: { BRL: 990, USD: 249 },
        params: {
            pattern: "stripes",
            primary: "#f5f5f5",
            secondary: "#1b1b1b",
            detail: "#c62828",
        },
    },
    {
        sku: "cosmetico-uniforme-banda",
        kind: "kit",
        name: "Uniforme Banda",
        description: "Banda diagonal amarela sobre azul.",
        prices: { BRL: 990, USD: 249 },
        params: {
            pattern: "sash",
            primary: "#10386b",
            secondary: "#f6c445",
            detail: "#ffffff",
        },
    },
    {
        sku: "cosmetico-uniforme-arcos",
        kind: "kit",
        name: "Uniforme Arcos",
        description: "Faixas horizontais em verde e branco.",
        prices: { BRL: 990, USD: 249 },
        params: {
            pattern: "hoops",
            primary: "#0f6b3f",
            secondary: "#f2f7f4",
            detail: "#0b3d24",
        },
    },
    {
        sku: "cosmetico-uniforme-xadrez",
        kind: "kit",
        name: "Uniforme Xadrez",
        description: "Quadriculado em bordô e areia.",
        prices: { BRL: 990, USD: 249 },
        params: {
            pattern: "checks",
            primary: "#7a2230",
            secondary: "#e4cfa3",
            detail: "#2b1216",
        },
    },
    // Temas de estádio -------------------------------------------------------
    {
        sku: "cosmetico-estadio-noite",
        kind: "stadium",
        name: "Estádio Noite de Decisão",
        description: "Gramado escuro, arquibancada cheia e refletor branco.",
        prices: { BRL: 1290, USD: 349 },
        params: {
            grass: "#123d24",
            stands: "#1a1f2b",
            sky: "#070a12",
            lights: "#eaf2ff",
        },
    },
    {
        sku: "cosmetico-estadio-tarde",
        kind: "stadium",
        name: "Estádio Tarde de Domingo",
        description: "Sol baixo, gramado claro e arquibancada de concreto.",
        prices: { BRL: 1290, USD: 349 },
        params: {
            grass: "#2f7a3f",
            stands: "#8d8578",
            sky: "#f0b56a",
            lights: "#fff3d6",
        },
    },
    {
        sku: "cosmetico-estadio-inverno",
        kind: "stadium",
        name: "Estádio Inverno",
        description: "Céu cinza, gramado fechado e luz fria.",
        prices: { BRL: 1290, USD: 349 },
        params: {
            grass: "#1f5a3a",
            stands: "#3f4750",
            sky: "#9aa7b4",
            lights: "#dff1ff",
        },
    },
    // Títulos ----------------------------------------------------------------
    {
        sku: "cosmetico-titulo-veterano",
        kind: "title",
        name: "Título Veterano",
        description: 'Mostra "Veterano" ao lado do seu nome.',
        prices: { BRL: 590, USD: 149 },
        params: { label: "Veterano", color: "#c9a227" },
    },
    {
        sku: "cosmetico-titulo-tatico",
        kind: "title",
        name: "Título Tático",
        description: 'Mostra "Tático" ao lado do seu nome.',
        prices: { BRL: 590, USD: 149 },
        params: { label: "Tático", color: "#4aa3df" },
    },
    {
        sku: "cosmetico-titulo-formador",
        kind: "title",
        name: "Título Formador",
        description: 'Mostra "Formador" ao lado do seu nome.',
        prices: { BRL: 590, USD: 149 },
        params: { label: "Formador", color: "#3fae6f" },
    },
    {
        sku: "cosmetico-titulo-torcedor",
        kind: "title",
        name: "Título Torcedor Raiz",
        description: 'Mostra "Torcedor Raiz" ao lado do seu nome.',
        prices: { BRL: 590, USD: 149 },
        params: { label: "Torcedor Raiz", color: "#e07a5f" },
    },
];
/** Índice por `sku`, que é como o resto do sistema chega a um cosmético. */
const POR_SKU = new Map(COSMETICS.map((item) => [item.sku, item]));
export function cosmeticBySku(sku) {
    return POR_SKU.get(sku) ?? null;
}
/** Os cosméticos de um tipo, na ordem do catálogo. */
export function cosmeticsOfKind(kind) {
    return COSMETICS.filter((item) => item.kind === kind);
}
// ---------------------------------------------------------------------------
// O que a pessoa tem e o que está usando
// ---------------------------------------------------------------------------
/**
 * A escolha de qual cosmético usar em cada lugar.
 *
 * Guarda `sku` e não identificador de produto porque a escolha sobrevive a uma
 * resemeadura: o produto ganha outro ULID, o `sku` continua o mesmo. `null` é
 * "nenhum", que é o estado de quem comprou e ainda não vestiu.
 */
export const CosmeticLoadoutSchema = z
    .object({
    crest: z.string().max(64).nullable(),
    kit: z.string().max(64).nullable(),
    stadium: z.string().max(64).nullable(),
    title: z.string().max(64).nullable(),
})
    .strict();
/** Nada escolhido, que é como toda conta começa. */
export const EMPTY_LOADOUT = {
    crest: null,
    kit: null,
    stadium: null,
    title: null,
};
/** Um cosmético que a pessoa possui, como `GET /me/cosmetics` devolve. */
export const OwnedCosmeticSchema = z.object({
    /** Identificador do produto, que é o que vira `cosmetic:<id>` em `entitlements`. */
    productId: IdSchema,
    sku: z.string().min(1),
    kind: CosmeticKindSchema,
    name: z.string().min(1),
    description: z.string().min(1),
    /** Se este é o que está em uso no lugar dele. */
    equipped: z.boolean(),
    params: z.union([CrestParamsSchema, KitParamsSchema, StadiumParamsSchema, TitleParamsSchema]),
});
/**
 * Cosméticos já resolvidos de alguém, prontos para desenhar.
 *
 * É esta forma que viaja junto de cada membro da liga e de cada linha do
 * leaderboard. Sai do servidor já filtrada pelo direito: o que não foi comprado
 * não chega ao cliente, então nem existe a tentação de o cliente decidir.
 */
export const AppliedCosmeticsSchema = z.object({
    crest: CrestParamsSchema.nullable(),
    kit: KitParamsSchema.nullable(),
    stadium: StadiumParamsSchema.nullable(),
    title: TitleParamsSchema.nullable(),
});
/** `GET /me/cosmetics` e `PUT /me/cosmetics`. */
export const MyCosmeticsSchema = z.object({
    owned: z.array(OwnedCosmeticSchema),
    loadout: CosmeticLoadoutSchema,
    /** Já resolvido em parâmetros, para a tela desenhar sem consultar catálogo. */
    applied: AppliedCosmeticsSchema,
});
/** `PUT /me/cosmetics`: só os campos que a pessoa quer mexer. */
export const UpdateLoadoutRequestSchema = CosmeticLoadoutSchema.partial().strict();
/** Ninguém comprou nada, que é a resposta para a esmagadora maioria das contas. */
export const NO_COSMETICS = {
    crest: null,
    kit: null,
    stadium: null,
    title: null,
};
/**
 * Resolve uma escolha em parâmetros, deixando de fora o que não foi comprado.
 *
 * `possui` é o conjunto de `sku` que o servidor confirmou no banco. Passar um
 * conjunto vazio devolve tudo nulo, que é exatamente o comportamento desejado
 * para quem perdeu o direito por estorno.
 */
export function applyLoadout(loadout, possui) {
    if (!loadout)
        return NO_COSMETICS;
    const escolhido = (sku, kind) => {
        if (!sku || !possui.has(sku))
            return null;
        const item = cosmeticBySku(sku);
        if (!item || item.kind !== kind)
            return null;
        return item.params;
    };
    return {
        crest: escolhido(loadout.crest, "crest"),
        kit: escolhido(loadout.kit, "kit"),
        stadium: escolhido(loadout.stadium, "stadium"),
        title: escolhido(loadout.title, "title"),
    };
}
//# sourceMappingURL=cosmetics.js.map